import { convertStringToDocNode, getGqlString, graphServerUrl, mergeAdditionalHeaders } from '.';
import {
  ApolloClient,
  DocumentNode,
  ErrorLike,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  LinkError,
  LocalStateError,
  ServerError,
  ServerParseError,
  UnconventionalError,
} from '@apollo/client';
import envData from '@/.pre-build/env.json';
import flattenedQueries from '@/.pre-build/flattened-queries.json';
import Logger from '@/utils/logger';
import LZString from 'lz-string';

export type QueryResultWithErrors = ApolloClient.QueryResult<unknown> & {
  error?: ErrorLike & { cause?: { message?: string }, errors: Array<never> }
};

const logTruncationLen = parseInt(process.env.GRAPHQL_LOG_TRUNCATION_LEN || '-1');
const forceQueryCompletionSummary = process.env.GRAPHQL_LOG_FORCE_SUMMARY === 'true';

const isCalledFromBrowser = typeof window !== 'undefined';
const logger = new Logger({ caller: 'utils.graphql.feedback' });

const truncateIfNeeded = (str: string, len = logTruncationLen) => {
  if (len > 0 && str.length > len) {
    return str.substring(0, len) + '...';
  }
  return str;
};

const getQueryName = (query: DocumentNode | string) => {
  // If we don't ignore TypeScript here, an error is thrown: `Property 'name' does not exist on type 'DefinitionNode'. Property 'name' does not exist on type 'SchemaDefinitionNode'.`
  // This is apparently because the type definitions for DocumentNode are not correct.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-next-line
  return typeof query === 'string' ? '' : query?.definitions?.[0]?.name?.value ?? '';
};

const getHeadersForDebugFeedback = (additionalHeaders: object = {}) => {
  const merged = mergeAdditionalHeaders({}, additionalHeaders).headers;
  if (isCalledFromBrowser && envData.deployedEnvName === 'prod') {
    // If needed, redacting headers when running in-browser, as they may contain sensitive data
    return Object.keys(merged).length > 0 ? { _redacted: true } : {};
  }
  return merged;
};

const getFlattenedQuery = (query: string | DocumentNode): string => {
  if (typeof query !== 'string') {
    query = getGqlString(query) || '';
  }
  return (flattenedQueries as { [key: string]: string })[query.trim()] as string || query;
};

// Based on: https://github.com/apollographql/apollo-studio-community/discussions/127#discussioncomment-2152550
const getPlaygroundUrl = (query: DocumentNode | string, variables: object, headers: object) => {
  const queryAsString = getFlattenedQuery(query);

  // If imports are unresolved, we can't generate a useful URL
  if (queryAsString.indexOf('#import') >= 0) {
    return '';
  }

  const includeCookies = false;

  const explorerURLState = LZString.compressToEncodedURIComponent(
    JSON.stringify({
      document: queryAsString.trim(),
      variables: JSON.stringify(variables, null, 2),
      headers: JSON.stringify(headers),
      includeCookies: String(includeCookies),
    }),
  );

  return `${graphServerUrl}/explorer?explorerURLState=${explorerURLState}`;
};

// Docs here: <https://www.apollographql.com/docs/react/data/error-handling#identifying-error-types>
const getErrorType = (error: unknown) => {
  if (CombinedGraphQLErrors.is(error)) {
    return 'CombinedGraphQLErrors';
  } else if (CombinedProtocolErrors.is(error)) {
    return 'CombinedProtocolErrors';
  } else if (LocalStateError.is(error)) {
    return 'LocalStateError';
  } else if (ServerError.is(error)) {
    // HTTP errors
    return error.statusCode.toString().match(/^5[\d]{2}$/) ? 'InternalServerError' : 'ServerError';
  } else if (LinkError.is(error)) {
    return 'LinkError';
  } else if (ServerParseError.is(error)) {
    // JSON parse errors
    return 'ServerParseError';
  } else if (UnconventionalError.is(error)) {
    // Errors thrown by irregular types
    return 'UnconventionalError';
  } else {
    return 'UnknownError';
  }
};

const isIntermittentNetworkError = (error: unknown) => {
  const errorType = getErrorType(error);
  return (
    [ 'LinkError', 'CombinedProtocolErrors', 'ServerParseError' ].includes(errorType) ||
    // @ts-expect-error "unknown" type doesn't know about `message` prop.
    (isCalledFromBrowser && error?.message === 'NetworkError when attempting to fetch resource.') ||
    (!isCalledFromBrowser && errorType === 'InternalServerError')
  );
};

const getDurationMessage = (queryStartTime?: number) => {
  if (queryStartTime) {
    const duration = Date.now() - queryStartTime;
    return ` for ${duration.toLocaleString()}ms`;
  }
  return '';
};

const getSafePlaygroundLink = async (
  query: DocumentNode | string,
  variables: object,
  additionalHeaders: object,
) => {
  const queryAsDocNode = typeof query === 'string' ? await convertStringToDocNode(query) : query;
  const headers = getHeadersForDebugFeedback(additionalHeaders);
  return getPlaygroundUrl(queryAsDocNode, variables, headers);
};

const getQueryDebugInfo = async (
  headline: string,
  query: DocumentNode | string,
  variables: object,
  additionalHeaders: object,
  result?: QueryResultWithErrors | undefined,
) => {
  const queryAsString = typeof query === 'string' ? query : getGqlString(query);
  const queryAsDocNode = typeof query === 'string' ? await convertStringToDocNode(query) : query;
  const headers = getHeadersForDebugFeedback(additionalHeaders);
  const playgroundUrl = await getSafePlaygroundLink(query, variables, additionalHeaders);

  headline = headline.replace(/\{queryName\}/g, getQueryName(queryAsDocNode) || '[unspecified]');

  const msg = [
    headline,
    `Queried:\n${graphServerUrl}`,
    `Try it out in our GraphQL playground here:\n${playgroundUrl || '[Unavailable due to unresolved imports]'}`,
  ];

  if (result?.error) {
    const errorMsg = [ ...new Set([
      getErrorType(result.error),
      result?.error.toString(),
      result?.error?.cause?.message,
    ]) ].filter((msg) => !!msg);

    if (errorMsg[1]?.indexOf(errorMsg[0] + ':') === 0) {
      errorMsg.shift();
    }

    let stringified = errorMsg.join(' :: ');

    if (result?.error?.errors?.length) {
      stringified += '\n' + JSON.stringify(result.error.errors, null, 2);
    }

    msg.push(`Was data returned? ${(!!result?.data).toString()}`);
    msg.push(`Returned error(s):\n${truncateIfNeeded(stringified, 500)}`);
  }

  if (!playgroundUrl) {
    msg.push(`Operation:\n${queryAsString}`);
  }

  if (Object.keys(variables).length > 0) {
    msg.push(`Variables:\n${JSON.stringify(variables, null, 2)}`);
  }

  if (Object.keys(headers).length > 0) {
    msg.push(`Headers:\n${JSON.stringify(headers, null, 2)}`);
  }

  if (result?.data) {
    for (const fieldName of Object.keys(result.data)) {
      // @ts-expect-error shh, TypeScript
      const cacheDetails = result.data?.[fieldName]?._meta?.cacheDetails;
      if (cacheDetails) {
        msg.push(`Cache details:\n${JSON.stringify(cacheDetails, null, 2)}`);
        break;
      }
    }
  }

  return truncateIfNeeded( msg.join('\n\n') );
};

const throwDataIssueError = async (
  message: string,
  query: DocumentNode | string,
  variables: object,
  queryResult: object,
  additionalHeaders: object = {},
) => {
  throw new Error(
    await getQueryDebugInfo(message, query, variables, additionalHeaders, { data: queryResult }),
  );
};

const logDataIssueWarning = async (
  message: string,
  query: DocumentNode | string,
  variables: object,
  queryResult: object,
  additionalHeaders: object = {},
) => {
  logger.warn(
    await getQueryDebugInfo(message, query, variables, additionalHeaders, { data: queryResult }),
  );
};

const getHumanReadableErrorMessage = async (
  response: QueryResultWithErrors,
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: object,
  queryStartTime?: number,
) => {
  return getQueryDebugInfo(`Error(s) running query {queryName}${getDurationMessage(queryStartTime)}.`, query, variables || {}, additionalHeaders || {}, response);
};

const completionMessage = async (
  cached: boolean | null,
  startTime: number,
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: object,
  data?: unknown,
) => {
  const duration = Date.now() - startTime;

  const getSummary = async (speed: string) => {
    const headline = cached === null ?
      `Returning ${speed} response after ${duration.toLocaleString()}ms for query {queryName}.` :
      `Returning ${speed} ${cached ? 'cached' : 'uncached'} response after ${duration.toLocaleString()}ms for query {queryName}.`;

    return getQueryDebugInfo(headline, query, variables || {}, additionalHeaders || {}, data ? { data } : undefined);
  };

  if (duration > 2000) {
    logger.info( await getSummary('🦥🦥🦥 VERY SLOW 🦥🦥🦥') );
  } else if (duration > 750) {
    logger.info( await getSummary('🦥 SLOW 🦥') );
  } else if (forceQueryCompletionSummary) {
    logger.info( await getSummary('') );
  }
};

export {
  completionMessage,
  getHumanReadableErrorMessage,
  getQueryName,
  isIntermittentNetworkError,
  logDataIssueWarning,
  logger,
  getSafePlaygroundLink,
  throwDataIssueError,
};
